<template>
  <div
    ref="popoverNotificationRef"
    class="atom-popover-notification"
    :class="[
      `is-type-${type}`,
      `is-position-${position}`,
    ]"
    @click="emit('on-hide')"
    @keypress="emit('on-hide')"
  >
    <div class="atom-popover-notification__inner">
      <div class="atom-popover-notification__message">
        <IonIcon
          v-if="icon"
          :icon-name="icon"
          :icon-color="type === 'success' ? 'var(--c-primary)' : 'var(--c-error)'"
          class="atom-popover-notification__icon"
          icon-size="22px"
        />

        <span>
          <AtomTextHeading 
            :text="title" 
            font-size="h3" 
            :font-colorh3="type === 'success' ? 'var(--c-primary)' : 'var(--c-error)'"
          />

          <div class="atom-popover-notification__text" v-html="text" />
        </span>
      </div>

      <button
        class="atom-popover-notification__close"
        type="button"
        @click="emit('on-hide')"
      >
        <IonIcon
          :icon-name="isMobile ? 'close' : 'close-small'"
          icon-color="var(--c-white)"
          :icon-size="isMobile ? '6px' : '10px'"
        />
      </button>
    </div>
  </div>
</template>

<script setup>
const props = defineProps({
    title: {
        type: String,
        default: '',
    },

    text: {
        type: String,
        default: '',
    },

    type: {
        type: String,
        default: 'success',
        validator: (value) => ['success', 'error'].includes(value),
    },

    useClickOutside: {
        type: Boolean,
        default: false,
    },

    position: {
        type: String,
        default: 'fixed',
        validator: (value) => ['fixed', 'absolute'].includes(value),
    },
});

/*
      Variables
  */
const { type } = toRefs(props);
const emit = defineEmits(['on-hide']);
const icon = ref(null);

watchEffect(() => {
    switch (type.value) {
    case 'success':
        icon.value = 'checkmark-circle';
        break;
    case 'error':
        icon.value = 'crossmark-circle';
        break;
    default:
        icon.value = null;
        break;
    }
});

onMounted(() => {
    window.setTimeout(() => {
        emit('on-hide');
    }, 60000); // Close the notification after 60 seconds
});

const popoverNotificationRef = ref(null);
if (props.useClickOutside) {
    onMounted(() => {
        onClickOutside(popoverNotificationRef, () => {
            emit('on-hide');
        });
    });
}
</script>

<style lang="scss" scoped>
.atom-popover-notification {
    @include z-index('formNotification');

    position: absolute;
    top: 0px;
    right: 25px;
    left: 25px;
    width: 438px;
    max-width: calc(100% - 50px);
    border-radius: var(--b-radius-500);
    margin: auto;
    backdrop-filter: blur(5px);
    background-color: var(--c-white);
    border-top-left-radius: 0px;
    border-top-right-radius: 0px;
    box-shadow:  0px 0px var(--box-shadow-unit--regular) 0px var(--c-boxshadow);
    cursor: pointer;
    transition: opacity 0.3s ease-in-out;

    &.is-position-fixed {
        position: fixed;
        top: 0;
        right: 0;
        left: 0;
        width: calc(100% - 50px);
        max-width: 438px;
    }
}



.atom-popover-notification__text ::v-deep(a) {
    color: var(--c-primary) !important;
    text-decoration: underline;
    text-underline-offset: 4px;
}

.atom-popover-notification__inner {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    padding: 20px;
    font-family: var(--f-family--primary);
    font-size: var(--f-size--description);
    font-weight: var(--f-weight--500);
    line-height: var(--l-height--description);

    .is-type-error & {
        color: var(--c-error);
    }

    .is-type-success & {
        color: var(--c-success);
    }

    .is-type-warning & {
        color: var(--c-warning);
    }
}

.atom-popover-notification__message {
    @include fluid('column-gap', 10px, 20px, 20px);

    display: flex;
    align-items: center;

    @include tablet {
        @include fluid('padding-left', 10px, 10px, 10px);
        @include fluid('padding-right', 10px, 10px, 10px);
    }
}

.atom-popover-notification__close {
    display: flex;
    width: 30px;
    min-width: 30px;
    height: 30px;
    min-height: 30px;
    align-items: center;
    justify-content: center;
    border-radius: var(--b-radius--full);
    background: var(--c-primary);
}
</style>
